import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

const dayjs = require('dayjs');
const customParseFormat = require('dayjs/plugin/customParseFormat');

dayjs.extend(customParseFormat);

const AgendaTable = ({ agenda, sessions: sessionsById, setSessions }) => {
    const [tracks, setTracks] = React.useState(null);

    if (tracks === null && agenda !== null) {
        setTracks(
            Array.from(
                Object.values(agenda).reduce(
                    (accumulator, sessions) =>
                        accumulator.union(new Set(sessions.map((session) => session.track).filter((t) => t))),
                    new Set()
                )
            )
        );
    }

    const sessionsByTrack = useCallback(
        (sessions) => {
            const ordered = [];

            tracks.forEach((track) => {
                ordered.push(sessions.find((session) => session.track === track));
            });

            if (ordered.filter((s) => typeof s !== 'undefined').length === 0) {
                ordered.splice(0, tracks.length, ...sessions);
            }

            return ordered;
        },
        [tracks]
    );

    const selectSession = (sessionId) => {
        setSessions((prevSessions) => ({
            ...prevSessions,
            [sessionId]: {
                ...prevSessions[sessionId],
                isSelected: !prevSessions[sessionId].isSelected,
            },
        }));
    };

    const updateAdditionalInformation = (sessionId, additionalInformation) => {
        setSessions((prevSessions) => ({
            ...prevSessions,
            [sessionId]: {
                ...prevSessions[sessionId],
                additionalInformation,
            },
        }));
    };

    if (tracks === null) {
        return null;
    }

    return (
        <table className={`agenda agenda--${tracks.length}-column`}>
            <tbody>
                <tr className="agenda__trackNames">
                    <th>Time</th>
                    {!tracks.length && (
                        <th>
                            <span>Sessions</span>
                        </th>
                    )}
                    {!!tracks.length &&
                        tracks.map((track) => (
                            <th key={`track-${track}`}>
                                <span>{track}</span>
                            </th>
                        ))}
                </tr>

                {Object.entries(agenda).map(([time, sessions]) => (
                    <tr key={`agenda-${time}`} className="agenda__session">
                        <td>{dayjs(time, 'HH:mm').format('h:mma')}</td>
                        {sessionsByTrack(sessions).map((session) => (
                            <td
                                colSpan={Math.abs(sessions.length - (tracks.length + 1))}
                                key={`session-${session.sessionId}`}
                            >
                                <div className="checkboxes">
                                    <div key={`session-${session.id}`}>
                                        {session.sessionType !== 'Networking Session' && (
                                            <input
                                                className="checkbox__input"
                                                type="checkbox"
                                                id={session.sessionId}
                                                checked={sessionsById[session.sessionId].isSelected}
                                                onChange={() => selectSession(session.sessionId)}
                                            />
                                        )}

                                        <label className="checkbox__label" htmlFor={session.sessionId}>
                                            <span>{session.sessionTitle}</span>
                                            <br />
                                            <em>
                                                {dayjs(session.sessionStartTime, 'HH:mm:ss').format('h:mma')}–
                                                {dayjs(session.sessionEndTime, 'HH:mm').format('h:mma z')}
                                            </em>
                                        </label>
                                    </div>
                                </div>
                                {session.sessionType === 'Meal Session' && (
                                    <div className="text">
                                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                        <label>Additional Information</label>
                                        <input
                                            className="text__input"
                                            type="text"
                                            placeholder="Dietary restrictions, special requirements, etc"
                                            value={sessionsById[session.sessionId].additionalInformation}
                                            onChange={(e) =>
                                                updateAdditionalInformation(session.sessionId, e.target.value)
                                            }
                                        />
                                    </div>
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

AgendaTable.propTypes = {
    agenda: PropTypes.object,
    sessions: PropTypes.object,
    setSessions: PropTypes.func,
};

export default AgendaTable;
